/**
 * Kit Create/Edit Dialog
 * Allows users to create or edit kit templates
 */
import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Alert,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Tooltip,
} from '@mui/material';
import {
  Close as CloseIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material';
import { rawMaterialKitsAPI, rawMaterialsAPI } from '../../services/api';

const KitCreateEditDialog = ({ open, onClose, kit, organization, onSuccess }) => {
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    items: [],
  });
  const [availableItems, setAvailableItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingItems, setLoadingItems] = useState(true);
  const [error, setError] = useState('');
  const [selectedItemId, setSelectedItemId] = useState('');
  const [selectedQuantity, setSelectedQuantity] = useState(1);

  useEffect(() => {
    if (open) {
      if (kit) {
        // Edit mode - load kit data
        setFormData({
          name: kit.name || '',
          description: kit.description || '',
          items: (kit.kitItems || []).map(item => ({
            rawMaterialItemId: item.rawMaterialItemId,
            rawMaterialItem: item.rawMaterialItem,
            quantity: item.quantity,
          })),
        });
      } else {
        // Create mode - reset form
        setFormData({
          name: '',
          description: '',
          items: [],
        });
      }
      fetchAvailableItems();
    }
  }, [open, kit]);

  const fetchAvailableItems = async () => {
    try {
      setLoadingItems(true);
      const response = await rawMaterialsAPI.getItems(organization);
      if (response.success) {
        setAvailableItems(response.data.filter(item => item.isActive) || []);
      }
    } catch (err) {
      console.error('Failed to fetch items:', err);
      setError('Failed to load raw material items');
    } finally {
      setLoadingItems(false);
    }
  };

  const handleAddItem = () => {
    if (!selectedItemId || selectedQuantity < 1) {
      setError('Please select an item and enter a quantity');
      return;
    }

    const item = availableItems.find(i => i.id === selectedItemId);
    if (!item) {
      setError('Selected item not found');
      return;
    }

    // Check if item already in kit
    if (formData.items.some(i => i.rawMaterialItemId === selectedItemId)) {
      setError('This item is already in the kit');
      return;
    }

    setFormData(prev => ({
      ...prev,
      items: [
        ...prev.items,
        {
          rawMaterialItemId: selectedItemId,
          rawMaterialItem: item,
          quantity: selectedQuantity,
        },
      ],
    }));

    setSelectedItemId('');
    setSelectedQuantity(1);
    setError('');
  };

  const handleRemoveItem = (index) => {
    setFormData(prev => ({
      ...prev,
      items: prev.items.filter((_, i) => i !== index),
    }));
  };

  const handleUpdateQuantity = (index, quantity) => {
    if (quantity < 1) return;
    setFormData(prev => ({
      ...prev,
      items: prev.items.map((item, i) => i === index ? { ...item, quantity } : item),
    }));
  };

  const handleSave = async () => {
    if (!formData.name.trim()) {
      setError('Kit name is required');
      return;
    }

    if (formData.items.length === 0) {
      setError('Kit must contain at least one item');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const payload = {
        name: formData.name.trim(),
        description: formData.description.trim() || null,
        organization,
        items: formData.items.map(item => ({
          rawMaterialItemId: item.rawMaterialItemId,
          quantity: item.quantity,
        })),
      };

      let response;
      if (kit) {
        response = await rawMaterialKitsAPI.updateKit(kit.id, payload);
      } else {
        response = await rawMaterialKitsAPI.createKit(payload);
      }

      if (response.success) {
        onSuccess?.(response.data);
        onClose();
      } else {
        setError(response.message || 'Failed to save kit');
      }
    } catch (err) {
      console.error('Failed to save kit:', err);
      setError(err.message || 'Failed to save kit');
    } finally {
      setLoading(false);
    }
  };

  const getAvailableItemsForSelect = () => {
    const usedIds = formData.items.map(item => item.rawMaterialItemId);
    return availableItems.filter(item => !usedIds.includes(item.id));
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ bgcolor: '#039582', color: 'white', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="h6" fontWeight={600}>
          {kit ? 'Edit Kit' : 'Create New Kit'}
        </Typography>
        <IconButton onClick={onClose} sx={{ color: 'white' }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ pt: 3 }}>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
            {error}
          </Alert>
        )}

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          <TextField
            fullWidth
            label="Kit Name *"
            value={formData.name}
            onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
            required
          />

          <TextField
            fullWidth
            label="Description"
            value={formData.description}
            onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
            multiline
            rows={2}
          />

          <Box>
            <Typography variant="subtitle1" fontWeight={600} gutterBottom>
              Kit Items *
            </Typography>
            
            {loadingItems ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                <CircularProgress size={24} />
              </Box>
            ) : (
              <>
                <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Select Raw Material Item</InputLabel>
                    <Select
                      value={selectedItemId}
                      label="Select Raw Material Item"
                      onChange={(e) => setSelectedItemId(e.target.value)}
                    >
                      {getAvailableItemsForSelect().map((item) => (
                        <MenuItem key={item.id} value={item.id}>
                          {item.itemDescription}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  <TextField
                    type="number"
                    label="Quantity"
                    value={selectedQuantity}
                    onChange={(e) => setSelectedQuantity(Math.max(1, parseInt(e.target.value) || 1))}
                    inputProps={{ min: 1 }}
                    sx={{ width: 120 }}
                    size="small"
                  />
                  <Button
                    variant="contained"
                    startIcon={<AddIcon />}
                    onClick={handleAddItem}
                    disabled={!selectedItemId}
                    sx={{ bgcolor: '#039582', '&:hover': { bgcolor: '#027A6B' } }}
                  >
                    Add
                  </Button>
                </Box>

                {formData.items.length === 0 ? (
                  <Alert severity="info">
                    No items added yet. Add at least one item to create the kit.
                  </Alert>
                ) : (
                  <TableContainer component={Paper} variant="outlined">
                    <Table size="small">
                      <TableHead>
                        <TableRow sx={{ bgcolor: '#f8fafc' }}>
                          <TableCell><strong>Item Description</strong></TableCell>
                          <TableCell align="right"><strong>Quantity</strong></TableCell>
                          <TableCell align="right" width={80}></TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {formData.items.map((item, index) => (
                          <TableRow key={index}>
                            <TableCell>{item.rawMaterialItem?.itemDescription || 'Loading...'}</TableCell>
                            <TableCell align="right">
                              <TextField
                                type="number"
                                value={item.quantity}
                                onChange={(e) => handleUpdateQuantity(index, Math.max(1, parseInt(e.target.value) || 1))}
                                inputProps={{ min: 1 }}
                                size="small"
                                sx={{ width: 80 }}
                              />
                            </TableCell>
                            <TableCell align="right">
                              <Tooltip title="Remove item">
                                <IconButton
                                  size="small"
                                  color="error"
                                  onClick={() => handleRemoveItem(index)}
                                >
                                  <DeleteIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}
              </>
            )}
          </Box>
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} disabled={loading}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSave}
          disabled={loading || !formData.name.trim() || formData.items.length === 0}
          sx={{ bgcolor: '#039582', '&:hover': { bgcolor: '#027A6B' } }}
        >
          {loading ? <CircularProgress size={20} /> : kit ? 'Update Kit' : 'Create Kit'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default KitCreateEditDialog;
